import { useEffect, useState } from 'react';
import { Users, Check, ArrowRight } from 'lucide-react';
import { api, TrendingGarage } from '@/lib/api';
import { trackEvent } from '@/lib/analytics';

const ONBOARDING_DONE_KEY = 'mw-garage-onboarding-done';
const ONBOARDING_TRIGGER_KEY = 'mw-garage-onboarding-trigger';

/** Set this flag after registration to trigger the modal on next render */
export function triggerGarageOnboarding() {
  try {
    sessionStorage.setItem(ONBOARDING_TRIGGER_KEY, '1');
  } catch { /* ignore */ }
}

export function shouldShowOnboarding(): boolean {
  try {
    return (
      sessionStorage.getItem(ONBOARDING_TRIGGER_KEY) === '1' &&
      localStorage.getItem(ONBOARDING_DONE_KEY) !== '1'
    );
  } catch {
    return false;
  }
}

function markOnboardingDone() {
  try {
    localStorage.setItem(ONBOARDING_DONE_KEY, '1');
    sessionStorage.removeItem(ONBOARDING_TRIGGER_KEY);
  } catch { /* ignore */ }
}

export function GarageOnboarding() {
  const [visible, setVisible] = useState(() => shouldShowOnboarding());
  const [garages, setGarages] = useState<TrendingGarage[]>([]);
  const [joined, setJoined] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!visible) return;
    trackEvent('garage_onboarding_impression');
    api.garages.trending({ limit: 8, days: 14 }).then((res) => {
      setGarages(res.items);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [visible]);

  if (!visible) return null;

  const handleJoin = async (slug: string) => {
    try {
      await api.garages.join(slug);
      setJoined((prev) => new Set([...prev, slug]));
      trackEvent('garage_onboarding_join', { garage: slug });
    } catch { /* already joined or error */ }
  };

  const handleDone = () => {
    markOnboardingDone();
    trackEvent('garage_onboarding_complete', { joined_count: joined.size });
    setVisible(false);
  };

  const handleSkip = () => {
    markOnboardingDone();
    trackEvent('garage_onboarding_skip');
    setVisible(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="mx-4 w-full max-w-md rounded-2xl border border-border-primary bg-bg-secondary p-6 shadow-2xl">
        <div className="mb-1 flex items-center gap-2">
          <Users className="h-5 w-5 text-primary-400" />
          <h2 className="text-lg font-bold text-text-primary">Join Your Garages</h2>
        </div>
        <p className="mb-5 text-sm text-text-secondary">
          Garages are communities for enthusiasts. Join a few to see discussions in your feed.
        </p>

        {loading ? (
          <div className="flex h-48 items-center justify-center">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
          </div>
        ) : (
          <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
            {garages.map((g) => {
              const isJoined = joined.has(g.slug);
              return (
                <div
                  key={g.id}
                  className="flex items-center gap-3 rounded-lg border border-border-primary bg-bg-tertiary/50 px-3 py-2.5"
                >
                  <span className="text-xl">{g.emoji || '🏎️'}</span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-text-primary">{g.name}</p>
                    <p className="text-xs text-text-tertiary">
                      {g.memberCount.toLocaleString()} members · {g.newPosts} new posts
                    </p>
                  </div>
                  <button
                    onClick={() => handleJoin(g.slug)}
                    disabled={isJoined}
                    className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                      isJoined
                        ? 'bg-green-500/15 text-green-400'
                        : 'bg-primary-500 text-white hover:bg-primary-600'
                    }`}
                  >
                    {isJoined ? (
                      <span className="flex items-center gap-1">
                        <Check className="h-3 w-3" /> Joined
                      </span>
                    ) : (
                      'Join'
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-5 flex items-center justify-between">
          <button
            onClick={handleSkip}
            className="text-sm text-text-tertiary hover:text-text-secondary"
          >
            Skip for now
          </button>
          <button
            onClick={handleDone}
            className="flex items-center gap-1.5 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600"
          >
            Continue <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
